import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const adjustMediaSkill: Skill = {
    name: SKILL_NAMES.adjustMedia,
    description:
        'Load BEFORE adjusting an existing clip: framing (subject cut off, bad crop, must show the whole image, needs zoom or motion, too bright under text), playback (a short clip loops, wrong trim, too fast/slow), or the look of one clip (colour grading, contrast, saturation, blur, vignette, grain).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Adjusting Media (framing, playback & look)

Every adjustment to a clip that is already in the slot goes through \`${TOOL_NAMES.editMedia}\`.
To put a *different* clip in the slot, use \`${TOOL_NAMES.setMedia}\` — that is all it does.

A media asset's frame is fixed by the design — you reframe the media *within* it
using \`${TOOL_NAMES.editMedia}\` (scale, position, Ken Burns, brightness).

## Fit or fill presets
- \`framing: 'fill'\` resets the media to cover its frame (scale 100%, centered, edges
  cropped) — the quickest way back to a clean crop after zooming or panning.
- \`framing: 'fit'\` zooms out until the whole media is visible inside the frame, centered,
  with empty space (the scene background) around it — use it for logos, slides, screenshots,
  charts and anything that must not be cropped.
- The video state reports \`framing\` as \`fit\` or \`custom\`; no \`framing\` means the media
  fills its frame. A preset can't be combined with \`scale\`/\`position\` in the same call:
  apply the preset first, then nudge.

## Rotate and flip
- \`rotation\` (degrees, -90 to 90, positive = clockwise) straightens a tilted photo or adds a
  deliberate tilt. On media that covers its frame, rotating raises the scale just enough to keep
  the corners covered — expect a small zoom-in.
- \`flip_horizontal\` mirrors the media left-to-right — use it so a person or product faces
  the text or the centre of the scene. \`flip_vertical\` mirrors top-to-bottom. Both are
  absolute states (true = mirrored, false = not), not toggles.
- The video state reports \`rotation\` (omitted when upright) and \`flipped\` (omitted when
  not mirrored).

## Framing a person in a talking-head recording
In a talking-head scene group, \`frame_speaker\` on \`${TOOL_NAMES.editMedia}\` frames a
person for you: face detection finds the faces in the recording at the scene's midpoint,
and \`frame_speaker: 1\` crops in close on the leftmost, \`2\` the next one to the right,
and so on. Prefer it to working out \`scale\`/\`position\` by hand whenever the subject is
a person. For a looser or tighter shot, pass \`scale\` in the same call: the face stays
centred at that zoom, whereas changing \`scale\` on its own afterwards drifts off it.
- **Two speakers side by side** (a split or stacked design with two media slots): frame
  speaker 1 in one slot and speaker 2 in the other. A slot that isn't already the talking
  head is linked to the recording, so both play the same moment in sync.
- **Never put the recording itself into a slot with \`${TOOL_NAMES.setMedia}\`.** That slot
  would get its own copy, playing from the start of the file, out of sync with the speech.
- It frames people only. For a shared screen or slide, or when it finds no face, crop by
  hand as below.
- **To frame a scene like another one**, copy that scene's \`scale\` and \`position\` from the
  video state. Don't estimate a position from an \`${TOOL_NAMES.imageAsk}\` answer: its pixel
  figures describe a small preview render, not \`position\` units.

## How positioning works
- Position translates the media relative to the center of its frame, bounded by the
  asset's \`mediaXTranslationRange\` and \`mediaYTranslationRange\` in the video state.
  Read the ranges first — a position outside them does nothing.
- **The axes are inverted from what you'd expect**: to move the media LEFT pass a
  positive x, RIGHT a negative x; UP a positive y, DOWN a negative y. (Think of it
  as moving the frame's window over the media, not the media itself.) So
  \`{ x: 10 }\` shifts it left, \`{ x: -10 }\` right, \`{ y: 15 }\` up, \`{ y: -15 }\` down.
- \`scale\` is a percentage: above 100 enlarges the media (width and height), below
  100 shrinks it. Scaling up zooms in and widens the translation ranges — if the
  range is too narrow to reach the crop you want, increase the scale first.

## The reframe loop
1. Read the asset's current scale, translation, and ranges from the video state.
2. Apply \`${TOOL_NAMES.editMedia}\` with the new scale/position.
3. Re-render the scene (\`${TOOL_NAMES.imageAsk}\`, optionally passing the media id too so the
   model can reason about how the media sits in its frame) and check the result.
4. Iterate until the composition lands.

## What good framing means
- The subject is well-composed: no cut-off faces or key objects, especially at the
  frame edges.
- Nothing important sits under text, captions, or overlaid assets — reposition the
  media (or dim it with \`brightness\` when it's a backdrop for text) rather than
  accepting a collision.
- **Ken Burns** (\`zoom_in\`, \`zoom_out\`, \`pan_*\`) adds motion to still images —
  useful when a photo has to carry a scene; \`none\` disables it.

## Playback (video clips only)
Read \`trimStartMs\`, \`trimEndMs\`, \`sourceDurationMs\`, \`playbackMode\` and \`playbackRate\`
from the video state to see how a clip is playing, then adjust with \`${TOOL_NAMES.editMedia}\`:
- **A short clip that repeats or freezes**: a clip shorter than its scene either loops
  (\`playbackMode: 'loop'\`) or holds its last frame (\`playbackMode: 'hold'\`). If a loop
  reads as a jarring restart, switch to \`hold\`; if a frozen frame looks broken, switch to
  \`loop\`. Better still, give the scene a clip that is long enough.
- **Trim** with \`start_time\`/\`end_time\` (milliseconds into the source clip) to keep only
  the moment you want. On a text-on-media scene the scene duration follows the trim, so
  trimming also shortens the scene.
- **Speed** with \`playback_rate\` (1 = normal, below 1 slower, above 1 faster).
- These apply to video only — they are ignored on images. To trim a voiceover or
  talking-head recording, use \`${TOOL_NAMES.trimSceneGroup}\` instead.

## Look (colour correction, effects and colour grading)
\`${TOOL_NAMES.setMediaEffects}\` adjusts how one clip looks without changing its framing or
playback. The video state shows the clip's current adjustments under \`effects\` (absent means
everything is at its default), including its colour grading override at \`effects.colorGrading\`
(absent means it follows the video's global filter, which \`${TOOL_NAMES.setGlobalColorGrading}\`
sets).
- **Colour correction**: \`contrast\` (1 = unchanged), \`hue\` (degrees), \`saturation\` and
  \`lightness\` (-1..1, 0 = unchanged). Nudge one or two values at a time — small moves
  (±0.2 saturation, ±0.3 contrast) read as a deliberate treatment; large ones look broken.
- **Effects**: \`blur\` (0..10) softens a backdrop so text on top stays legible — pair it
  with \`brightness\` from \`${TOOL_NAMES.editMedia}\` when dimming alone isn't enough;
  \`vignette\` (0..1) darkens the edges to pull the eye to the centre; \`noise\` (0..1) adds
  film grain.
- **A different grade on one clip** (a sepia flashback, one black-and-white photo): pass the
  filter's name as \`color_filter\` (and optionally \`color_filter_intensity\`). Mono is the
  greyscale filter — use it rather than \`saturation: -1\`.
- **Keep one clip ungraded** while the rest of the video is graded (a logo, a screenshot):
  \`color_filter: 'none'\`. **Undo the override** so the clip matches the rest of the video
  again: \`color_filter: 'global'\`.
- \`reset: true\` returns the look to defaults and drops any colour grading override; it leaves
  brightness and playback alone. Values passed in the same call apply on top.
- Re-render the scene (\`${TOOL_NAMES.imageAsk}\`) after a change and check it reads well.`;
    },
};
